import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import {
    showAemInsuranceRates,
    showAemMonthlyRates,
} from "../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { getUscCashPriceFromVehicle, showUscPrice, uscPriceIsExclVat } from "../../shared-logic/utils/uscPriceUtils";
import { getVehiclePriceSource } from "./priceUtils";
import type { VehicleType, UscDefaultInsuranceQuoteType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import type { UscInsuranceQuoteType } from "./insuranceUtils";
import { LEXUS_BRAND_ID, TOYOTA_BRAND_ID } from "../../shared-logic/utils/dfConstants";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";

/**
 * Monthly rate should be shown if:
 * - Flexibility matrix is available
 * - Used/Stock specific financeEnabled setting is true
 * - The current vehicle has a monthly rate and disclaimer
 * - The vehicle price is incl vat (or the setting to show excl vat prices is enabled)
 */
export const enableUscMonthlyRate = (
    commonSettings: CommonSettingsType,
    settings: UscSettingsType,
    vehicle: VehicleType | null,
    context: UscContext,
): boolean => {
    // Flexibility matrix will not be available if the global T1 finance setting is false so we don't need any additional checks for now.
    // ⚠️ Please also update the relevant UscDebugModal content if this ever changes ⚠️
    return (
        !!vehicle &&
        settings.enableFinance &&
        showAemMonthlyRates(commonSettings, AemFmComponent.Pdp, context) &&
        !!vehicle.financingOffers?.length &&
        !!vehicle.financeInfo &&
        (settings.enableExclVatMonthlyPrice ? true : !uscPriceIsExclVat(getUscCashPriceFromVehicle(vehicle))) &&
        !!settings.monthlyPaymentUrl
    );
};

export const enableUscInsurance = (
    commonSettings: CommonSettingsType,
    settings: UscSettingsType,
    vehicle: VehicleType | null,
    context: UscContext,
): boolean => {
    // ⚠️ Please also update the relevant UscDebugModal content if this ever changes ⚠️

    const isInsuranceEnabled = settings.enableInsurance;
    const showInsuranceRates = showAemInsuranceRates(commonSettings, AemFmComponent.Pdp, context);
    const hasInsuranceQuote = !!vehicle?.insuranceQuote;
    const isInsuranceQuoteRequired = settings.requireDefaultInsurance;
    const showPrice = showUscPrice(getVehiclePriceSource(vehicle));
    const isPriceInclVat = !uscPriceIsExclVat(getUscCashPriceFromVehicle(vehicle));
    const isToyotaVehicle = vehicle?.product.brand.code === TOYOTA_BRAND_ID;
    const isLexusVehicle = vehicle?.product.brand.code === LEXUS_BRAND_ID;

    /**
     * TES Edge Case:
     * There's currently an issue between DF & Makolab enrichment causing the default insurance rate
     * to be missing for _all_ TES vehicles. As a temporary fix, we configure requireDefaultInsurance
     * to be false - though this has the side effect of showing the insurance rate for all TES vehicles, regardless
     * of their brand. This will be replaced with a better solution in the future (a dedicated flag indicating insurance availability)
     */
    if (commonSettings.country === "es" && !isToyotaVehicle && !isLexusVehicle) return false;

    return (
        isInsuranceEnabled &&
        showInsuranceRates &&
        (isInsuranceQuoteRequired ? hasInsuranceQuote : true) &&
        showPrice &&
        isPriceInclVat
    );
};

export const isPersonalizedUscInsurance = (
    insuranceQuote: UscInsuranceQuoteType | UscDefaultInsuranceQuoteType,
): insuranceQuote is UscInsuranceQuoteType => {
    return (insuranceQuote as UscInsuranceQuoteType).quoteId !== undefined;
};
